import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { RealtimeMessage, SiteAlertState } from '@cloudsign/shared';
import { useLiveChannel } from '../../lib/realtime';
import type { DisplaySession } from './session';

/**
 * Live updates for the display. A display has no cookie, so it cannot use the portal's shared hub: it opens its own
 * Socket.IO connection with the device token (`auth: { deviceToken }`) and joins `display` (refresh messages, at most one
 * every 5 seconds), `site-alert` (evacuation start and end) and `occupancy` (live headcount) for its own site.
 * In tests and VITE_MOCK=1 the in-memory hub stands in for the socket.
 */

export const CHANNELS = ['display', 'site-alert', 'occupancy'] as const;
/** Wait before dialling again after the server refused the connection (for example a revoked token). */
const REDIAL_MS = 15_000;

export interface LiveHandlers {
  /** Something on the view changed: fetch it again. */
  onRefresh: () => void;
  /** A new evacuation state arrived on `site-alert`. */
  onAlert: (alert: SiteAlertState) => void;
  /** The server dropped this display (control message or refused connection): check the token. */
  onDropped: () => void;
}

function dispatch(channel: string, data: unknown, h: LiveHandlers): void {
  if (channel === 'site-alert') {
    const a = SiteAlertState.safeParse(data);
    if (a.success) h.onAlert(a.data);
    else h.onRefresh();
    return;
  }
  h.onRefresh();
}

function useSocketLive(session: DisplaySession, handlers: LiveHandlers): void {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  });
  const { token, siteId } = session;
  useEffect(() => {
    const socket = io({ path: '/api/socket.io', auth: { deviceToken: token }, reconnectionDelayMax: 10_000 });
    let first = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    socket.on('connect', () => {
      for (const channel of CHANNELS) socket.emit('subscribe', { channel, siteId }, () => undefined);
      // After a dropped connection the REST view is the truth; messages in between were missed.
      if (!first) ref.current.onRefresh();
      first = false;
    });
    socket.on('message', (raw: unknown) => {
      const m = RealtimeMessage.safeParse(raw);
      if (m.success && m.data.siteId === siteId) dispatch(m.data.channel, m.data.data, ref.current);
    });
    socket.on('control', () => ref.current.onDropped());
    socket.on('connect_error', () => {
      // A refusal ends automatic retries; look at the token, then dial again.
      ref.current.onDropped();
      if (!socket.active) timer = setTimeout(() => socket.connect(), REDIAL_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      socket.disconnect();
    };
  }, [token, siteId]);
}

function useMockLive(session: DisplaySession, handlers: LiveHandlers): void {
  const onMessage = (channel: string) => (data: unknown) => dispatch(channel, data, handlers);
  const reconnect = () => handlers.onRefresh();
  useLiveChannel('display', session.siteId, onMessage('display'), reconnect);
  useLiveChannel('site-alert', session.siteId, onMessage('site-alert'), reconnect);
  useLiveChannel('occupancy', session.siteId, onMessage('occupancy'), reconnect);
}

const useMock = import.meta.env.VITE_MOCK === '1' || import.meta.env.MODE === 'test';
export const useDisplayLive: (session: DisplaySession, handlers: LiveHandlers) => void = useMock ? useMockLive : useSocketLive;

// Re-exported so a message can be pushed in tests without importing the hub twice.
export { mockLive } from '../../lib/realtime';
